import { EMPTY_PARAMS, Params, convertParam } from '../pages/InstallationParams';

export type MeasurementSystem = 'IMPERIAL' | 'METRIC';

/** Camera params in installation_params_v1 (values may be number or string from API). */
export interface InstallationParamsV1Camera {
    identifier?: string;
    height_m?: number | string;
    depth_m?: number | string;
    offset_m?: number | string;
}

/** Nested installation params v1 returned by the switch/check-device API. */
export interface InstallationParamsV1 {
    version?: string;
    vehicle_params?: {
        weight_ton?: number | string;
        length_m?: number | string;
        hood_height_m?: number | string;
        width_m?: number | string;
        vehicle_type?: string;
    };
    camera_params?: {
        FrontFacingCamera?: InstallationParamsV1Camera;
        InCabinCamera?: { identifier?: string };
        LeftBlindSpotCamera?: InstallationParamsV1Camera;
        RightBlindSpotCamera?: InstallationParamsV1Camera;
        RearCamera?: InstallationParamsV1Camera;
    };
}

function toStr(v: number | string | undefined): string {
    if (v === undefined || v === null) return '';
    return typeof v === 'number' ? String(v) : v;
}

/**
 * Converts installation_params_v1 (nested) to the flat Record<string, string>
 * shape expected by buildParamsFromSwitchResponse.
 */
export function installationParamsV1ToFlat(v1: InstallationParamsV1): Record<string, string> {
    const flat: Record<string, string> = {};
    const vp = v1.vehicle_params;
    if (vp) {
        flat['vehicle-weight'] = toStr(vp.weight_ton);
        flat.vehicleLength = toStr(vp.length_m);
        flat.instHoodHeight = toStr(vp.hood_height_m);
        flat['vehicle-width'] = toStr(vp.width_m);
        flat.vehicleType = toStr(vp.vehicle_type);
    }
    const cp = v1.camera_params;
    if (cp?.FrontFacingCamera) {
        const ff = cp.FrontFacingCamera;
        flat.instHeight = toStr(ff.height_m);
        flat.instDepth = toStr(ff.depth_m);
        const offset = ff.offset_m;
        if (offset !== undefined && offset !== null && offset !== '') {
            const num = typeof offset === 'number' ? offset : parseFloat(offset);
            if (!Number.isNaN(num)) {
                flat.instHorizontalOffset = String(num);
            }
        }
    }
    if (cp?.LeftBlindSpotCamera) {
        const lb = cp.LeftBlindSpotCamera;
        flat.bsdLeftHeight = toStr(lb.height_m);
        flat.bsdLeftDepth = toStr(lb.depth_m);
    }
    if (cp?.RightBlindSpotCamera) {
        const rb = cp.RightBlindSpotCamera;
        flat.bsdRightHeight = toStr(rb.height_m);
        flat.bsdRightDepth = toStr(rb.depth_m);
    }
    return flat;
}

/** Switch/check-device API response shape (installation_params optional when v1 present). */
export interface SwitchResponseData {
    message?: string;
    installation_params?: Record<string, string>;
    installation_params_v1?: InstallationParamsV1;
}

/**
 * Returns flat installation params from the switch API response, preferring v1 when present.
 * Returns undefined if data is null/undefined or installation_params is null.
 */
export function getFlatParamsFromSwitchResponse(
    data: SwitchResponseData | null | undefined,
): Record<string, string> | undefined {
    if (data == null) {
        return undefined;
    }
    if (data.installation_params_v1) {
        return installationParamsV1ToFlat(data.installation_params_v1);
    }
    const params = data.installation_params;
    return params != null && typeof params === 'object' ? params : undefined;
}

const FIELDS_TO_CONVERT: (keyof Params)[] = [
    'instHeight',
    'instDepth',
    'instHoodHeight',
    'instHorizontalOffset',
    'vehicle-width',
    'vehicleLength',
    'bsdLeftDepth',
    'bsdLeftHeight',
    'bsdRightDepth',
    'bsdRightHeight',
];

/**
 * Builds Params from the install/switch API installation_params response.
 * Copies all matching keys, derives offsetDirection from instHorizontalOffset,
 * and converts numeric fields from METRIC to the customer's measurement system.
 */
export function buildParamsFromSwitchResponse(
    installationParams: Record<string, string> | null | undefined,
    licensePlate: string,
    measurementSystem: MeasurementSystem,
): Params {
    const updatedParams: Params = { ...EMPTY_PARAMS };

    if (installationParams != null && typeof installationParams === 'object') {
        for (const key of Object.keys(updatedParams) as (keyof Params)[]) {
            if (key in installationParams) {
                updatedParams[key] = installationParams[key] as Params[keyof Params];
            }
        }

        const instHorizontalOffsetStr = installationParams.instHorizontalOffset;
        if (instHorizontalOffsetStr) {
            const instHorizontalOffset = parseFloat(instHorizontalOffsetStr);
            updatedParams.offsetDirection = instHorizontalOffset < 0 ? 'left' : 'right';
            updatedParams.instHorizontalOffset = Math.abs(instHorizontalOffset).toString();
        }

        for (const field of FIELDS_TO_CONVERT) {
            const value = updatedParams[field];
            if (value !== '' && value !== undefined) {
                const num = parseFloat(value);
                if (!Number.isNaN(num)) {
                    (updatedParams as unknown as Record<string, string>)[field] = convertParam(
                        num,
                        'METRIC',
                        measurementSystem,
                    ).toString();
                }
            }
        }
    }

    updatedParams.license_plate = licensePlate;
    return updatedParams;
}
